import type { KeyMap } from 'react-hotkeys';

import type { ModalService } from './../ricos-modals';
import type { Decoration_Type, Node_Type } from './../ricos-schema/legacy/wix/rich_content/v1';

import type { TFunction } from './commonTypes';
import type { Platform } from './context';
import type { PublicCommands } from './editorCommandsType';
import type { BasicKeyCombination, ValidKeyCombination } from './key-types';
import type { TiptapEditor } from './tiptap';

export type LocalizedDisplayData = {
  name: KeyboardShortcut['name'];
  description: KeyboardShortcut['description'];
  keyCombinationText: string;
  tooltipHint: string;
  group: KeyboardShortcut['group'];
};

export type KeyboardShortcutId =
  | 'AddHorizontalTabulation'
  | 'Enter'
  | 'NewLine'
  | 'SoftNewLine'
  | 'SoftNewLineMeta'
  | 'SoftNewLineShift'
  | 'MoveToNextCellTab'
  | 'MoveToNextCellArrow'
  | 'MoveToPreviousCellTab'
  | 'MoveToPreviousCellArrow'
  | 'EnterExitCell'
  | 'AddNewLineShift'
  | 'AddNewLineAlt'
  | 'DropLineInList'
  | 'SelectContentInCell'
  | 'SelectColumn'
  | 'SelectRow'
  | 'SelectTable'
  | 'AddNewColumnRow'
  | 'DeleteColumnRow'
  | 'ExitToolbar'
  | 'EnterToolbar'
  | 'ApplySelection'
  | 'MoveSelectionToNextOption'
  | 'MoveSelectionToPreviousOption'
  | 'SelectNextBlock'
  | 'SelectPrevBlock'
  | 'MoveSelectionThroughElements'
  | 'OpenBlocksMenu'
  | 'SelectAll'
  | 'SelectTextBlocks'
  | 'ClearSelection'
  | 'MoveSelectedBlockUp'
  | 'MoveSelectedBlockDown'
  | 'OpenBlocksMenu'
  | 'OpenShortcutsSheet'
  | 'SelectNextBlockWithTab'
  | 'SelectNextBlockArrows'
  | 'ApplySelection'
  | 'ExitBlocksMenu'
  | 'MoveToNextElementTab'
  | 'MoveToNextElementEnter'
  | 'SelectEntireList'
  | 'TextAlignment.LEFT'
  | 'TextAlignment.CENTER'
  | 'TextAlignment.RIGHT'
  | 'TextAlignment.JUSTIFY'
  | 'UNDO'
  | 'REDO'
  | 'INDENT.increase'
  | 'INDENT.decrease'
  | 'PasteWithoutFormatting'
  | keyof typeof Node_Type
  | keyof typeof Decoration_Type
  | `${Decoration_Type.FONT_SIZE}.increase`
  | `${Decoration_Type.FONT_SIZE}.decrease`
  | `${Node_Type.HEADING}.${1 | 2 | 3 | 4 | 5 | 6}`
  | 'Empty'
  | 'OpenAiModal';

/**
 * Customizable keyboard shortcut for Return API, currently limited to `Enter`-related shortcuts.
 * Allows to remap `new line` / `soft new line` editor functionalities to `Enter`-based combination, or to remap `Enter` key to custom action.
 *
 * Examples:
 * ```ts
 *  {
 *    name: 'NewLine',
 *    description: 'Remap new line editor functionality to Shift+Enter',
 *    keys: { macOs: 'Shift+Enter', windows: 'Shift+Enter' },
 *    command: publicCommands => publicCommands.newLine()
 *  }, {
 *    name: 'Enter',
 *    description: 'Remap Enter key to custom action',
 *    keys: { macOs: 'Enter', windows: 'Enter' },
 *    command: () => submitForm(); // user defined action
 *  }, {
 *    name: 'SoftNewLine',
 *    description: 'Remap new line editor functionality to Ctrl+Enter',
 *    keys: { macOs: 'Meta+Enter', windows: 'Ctrl+Enter' },
 *    command: publicCommands => publicCommands.softNewLine()
 *  }
 * ```
 */
export type CustomizableKeyboardShortcut = {
  /**
   * `Enter`-related shortcut name
   *
   */
  name: 'NewLine' | 'SoftNewLine' | 'Enter';
  /**
   * Free text description, displayed in Shortcuts Sheet help dialog
   *
   */
  description: string;
  /**
   * Platform-dependent key combination
   *
   */
  keys: {
    macOs: 'Shift+Enter' | 'Enter' | 'Meta+Enter' | 'Ctrl+Enter';
    windows: 'Shift+Enter' | 'Enter' | 'Meta+Enter' | 'Ctrl+Enter';
  };
  /**
   * Command triggered by shortcut
   *
   */
  command: KeyboardShortcut['command'];
  enabled: KeyboardShortcut['enabled'];
  group: 'ShortcutsSheet_Section_Formatting';
};

/**
 * Keyboard shortcut configuration
 *
 * @export
 * @interface KeyboardShortcut
 */
export type KeyboardShortcut = {
  /**
   * Shortcut identifier, used as localized display data
   *
   * @type {string}
   * @memberof KeyboardShortcut
   */
  name: KeyboardShortcutId;
  /**
   * Shortcut description, used as localized display data
   *
   * @type {string}
   * @memberof KeyboardShortcut
   */
  description: string;
  /**
   * Shortcut group (context), used both as grouping key and localized display data
   *
   * @type {string}
   * @memberof KeyboardShortcut
   */
  group: string;
  /**
   * The shortcut keys like Control+B
   * Limited up to 2 modifiers and single Latin/number/some special character key
   *
   * The key names based on standard Web KeyboardEvent.key values:
   * https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values
   *
   * @type {BasicKeyCombination}
   * @memberof KeyboardShortcut
   */
  keys: {
    macOs: BasicKeyCombination;
    windows: BasicKeyCombination;
  };
  /**
   * Command for execution
   *
   * @memberof KeyboardShortcut
   */
  command: (args: {
    publicCommands: PublicCommands;
    editor: TiptapEditor;
    modalService: ModalService;
    event?: KeyboardEvent;
  }) => void; // TODO: define return value type
  /**
   * Enables shortcut
   *
   * @type {boolean}
   * @memberof KeyboardShortcut
   */
  enabled: boolean;
};

export type ValidatedShortcut<M extends string, W extends string> = Omit<
  KeyboardShortcut,
  'keys'
> & {
  keys: {
    macOs: ValidKeyCombination<M>;
    windows: ValidKeyCombination<W>;
  };
};

/**
 * Type checker for KeyboardShortcut
 *
 * @param {ValidatedShortcut} shortcut
 * @returns  {KeyboardShortcut} shortcut
 */
export const createShortcut = <M extends string, W extends string>(
  shortcut: ValidatedShortcut<M, W>
): KeyboardShortcut => shortcut as KeyboardShortcut;

export interface ShortcutDataProvider {
  /**
   * Provides shortcut display data by given shortcut name for current locale and platform
   *
   * @param {KeyboardShortcut['name']} name
   * @param {TFunction} t
   * @param {Platform} platform
   * @returns  {LocalizedDisplayData}
   * @memberof ShortcutDataProvider
   */
  getShortcutDisplayData(
    name: KeyboardShortcut['name'],
    t: TFunction,
    platform: Platform
  ): LocalizedDisplayData;

  getDisplayData(t: TFunction, platform: Platform): { [group: string]: LocalizedDisplayData[] };
}

export type HotKeysProps = {
  keyMap: KeyMap;
  handlers: { [name: string]: (e?: KeyboardEvent) => void };
  allowChanges: boolean;
};
